/**
 * FibrationPanel — π_I in motion.
 *
 * Renders the fibration diagram and lets the reader toggle between a
 * governed lift and three seam refusals (admission widened, issuer out
 * of scope, interpreter drift). Pairs each mode with the formal trigger
 * and the rulebook code that fires.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import {
  FibrationDiagram,
  type FibrationMode,
} from "../components/FibrationDiagram.js";

const ACCENT = "#5eead4";

const MODES = [
  { id: "governed", label: "Governed lift", color: panelTokens.accentGreen },
  {
    id: "ungoverned-admission",
    label: "Sᴬ — admission widened",
    color: panelTokens.accentRed,
  },
  {
    id: "ungoverned-authority",
    label: "Sᵁ — issuer out of scope",
    color: panelTokens.accentPurple,
  },
  {
    id: "ungoverned-meta",
    label: "Sᴹ — interpreter drift",
    color: panelTokens.accentBlue,
  },
] as const;

interface ModeDetail {
  readonly summary: string;
  readonly rule: string;
  readonly readsAs: string;
}

const DETAIL: Readonly<Record<FibrationMode, ModeDetail>> = {
  governed: {
    summary:
      "Every raw change c in 𝓥 admits a witness lift χ in 𝓔_I. The chain χ₂ ∘ χ₁ is itself a witness — temporal integrity holds compositionally.",
    rule: "(accept)",
    readsAs: "GovArrow ⊆ Mor(𝓔_I) is closed under composition.",
  },
  "ungoverned-admission": {
    summary:
      "The post-change admission boundary expanded. Sᴬ has no bridge: preservation cannot extend across an expanded admission, even if the issuer and interpreter are unchanged.",
    rule: "ILG-P-004 — ABS diff expanded/unknown",
    readsAs: "abs_diff_class ∈ {expanded, unknown} ⇒ refuse_preserve.",
  },
  "ungoverned-authority": {
    summary:
      "The issuer signing the witness has no in-scope ICAP for this artifact / kind / invariant_id. Sᵁ is bridgeable only by an ICAPExpansion (not retroactive); without one, the witness refuses.",
    rule: "ILG-IC-002 — Scope expansion without ICAPExpansion",
    readsAs: "MaySign(ICAP, Witness) = false ⇒ refuse_witness.",
  },
  "ungoverned-meta": {
    summary:
      "The interpreter / classifier / oracle drifted under this change. Sᴹ is bridgeable only by a RegimeMigrationArtifact paired with a Regression Oracle; without those, the witness cannot ride the lift.",
    rule: "ILG-P-005 — Drift severity high/unknown",
    readsAs: "DriftReport.severity ∈ {high, unknown} ⇒ refuse_preserve.",
  },
};

export function FibrationPanel(): React.JSX.Element {
  const [mode, setMode] = useState<FibrationMode>("governed");
  const detail = DETAIL[mode];

  return (
    <PanelSurface.PanelShell
      title="The Fibration"
      subtitle="π_I : 𝓔_I → 𝓥 — governed iff each raw change admits a witness lift"
      accentColor={ACCENT}
    >
      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Pick a lift to inspect</PanelSurface.SectionHeading>
        <div className="mt-3.5">
          <PanelSurface.ChipRow
            items={MODES.map((m) => ({ ...m }))}
            activeId={mode}
            onChange={(id) => {
              setMode(id);
            }}
            ariaLabel="Fibration mode"
          />
        </div>
        <div className="mt-[22px]">
          <FibrationDiagram mode={mode} />
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>What this lift says</PanelSurface.SectionHeading>
        <p className="mt-3.5 text-[14.5px] leading-[1.7] text-[var(--ilg-text)]">
          {detail.summary}
        </p>
        <div className="mt-3.5 flex gap-2.5 flex-wrap">
          <span className="text-[11px] font-mono text-[var(--ilg-muted)] px-2.5 py-1 border border-[var(--ilg-border)] rounded-full">
            rule: {detail.rule}
          </span>
        </div>
        <PanelSurface.SectionHeading style={{ marginTop: 22 }}>Reads as</PanelSurface.SectionHeading>
        <PanelSurface.Mono>{detail.readsAs}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>The fibration in one paragraph</PanelSurface.SectionHeading>
        <p className="mt-3.5 text-[14.5px] leading-[1.75] text-[var(--ilg-text)]">
          The base <PanelSurface.InlineCode>𝓥</PanelSurface.InlineCode> is the timeline: objects are system
          versions <PanelSurface.InlineCode>v₀, v₁, v₂, …</PanelSurface.InlineCode> and arrows are raw changes{" "}
          <PanelSurface.InlineCode>c : vᵢ → vᵢ₊₁</PanelSurface.InlineCode>. The total space <PanelSurface.InlineCode>𝓔_I</PanelSurface.InlineCode>{" "}
          sits over it: an object <PanelSurface.InlineCode>Eᵢ</PanelSurface.InlineCode> is an evidence bundle
          proving the invariant holds at <PanelSurface.InlineCode>vᵢ</PanelSurface.InlineCode>; an arrow{" "}
          <PanelSurface.InlineCode>χ : Eᵢ → Eᵢ₊₁</PanelSurface.InlineCode> is a <strong>witness lift</strong>{" "}
          certifying that the evidence travels along <PanelSurface.InlineCode>c</PanelSurface.InlineCode>. The
          projection <PanelSurface.InlineCode>π_I</PanelSurface.InlineCode> forgets the evidence and remembers
          only the version. A raw change <PanelSurface.InlineCode>c</PanelSurface.InlineCode> is{" "}
          <strong>governed</strong> iff the fibre <PanelSurface.InlineCode>π_I⁻¹(c)</PanelSurface.InlineCode> is
          non-empty — iff at least one witness lift exists. The four seams are
          the structural ways a fibre becomes empty.
        </p>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
